"use client";

import GeneralListingCard, {
  ListingCardLoading,
} from "@/components/listing-card/listing-card";
import ListingCardLayout, {
  ListingCardLayoutLoading,
} from "./components/listing-card-layout";
import styles from "./page.module.css";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { useMyListingsStore } from "./useMyListingsStore";
import { useBreakpoints } from "@/hooks/use-breakpoints";
import PreviewFrame from "@/components/preview-frame/preview-frame";
import { GetUserListingsResponse, UserListingCard } from "./types";
import { motion } from "framer-motion";
import { Pagination } from "@/components/pagination/pagination";
import PageSearchbar from "./components/searchbars/page-searchbar";
import { PurposeEnum, TypeEnum } from "@/types/types";
import MobileSearchbar from "./components/searchbars/mobile-searchbar";
import ListingSettingModal from "./components/modals/listing-settings/listing-settings-modal";
import SoldModal from "./components/modals/delete-modals/sold-modal";
import DeleteModal from "./components/modals/delete-modals/delete-modal";
import DeleteModalWrapper from "./components/modals/delete-modals/modal-wrapper";
import { useScrollLock } from "@/hooks/use-scroll-lock";
import { useSearchParams, useRouter, usePathname } from "next/navigation";

export default function MyListingsLayout({
  myListingsData,
}: {
  myListingsData: GetUserListingsResponse;
}) {
  const t = useTranslations("MyListings");
  const locale = useLocale();
  const { userData } = useUserAuthDataContext();
  const {
    state,
    initFirstPage,
    fetchPage,
    isRefetchingListingCardLoading,
    type,
    purpose,
    settingsModalListing,
    setSettingsModalListing,
  } = useMyListingsStore();
  useEffect(() => {
    // if (!isLoaded || locale !== listingsLocale) {
    const { state } = useMyListingsStore.getState();

    if (state.isLoaded) return;
    initFirstPage(myListingsData, locale);
    // }
  }, [locale, myListingsData]);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewListingId, setPreviewListingId] = useState("");
  const { isXs, isSm, isMd, isLg } = useBreakpoints();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const handledRef = useRef<string | null>(null);
  const listingId = searchParams.get("listingId");

  useEffect(() => {
    if (!listingId) return;

    if (handledRef.current === listingId) return;
    handledRef.current = listingId;
    const openFromUrl = async () => {
      const {
        state,
        setSettingsModalListing,
        getListingById,
        setPinnedListingNull,
      } = useMyListingsStore.getState();

      const existing = state.listings.find((l) => l.listing_id === listingId);

      if (existing) {
        setSettingsModalListing(existing);
        setIsModalOpen(true);
        setPinnedListingNull();
        router.replace(pathname, { scroll: false });
        return;
      }

      const listing = await getListingById(listingId, locale);

      if (listing) {
        setSettingsModalListing(listing);
        setIsModalOpen(true);
        setPinnedListingNull();
      }

      router.replace(pathname, { scroll: false });
    };

    openFromUrl();
  }, [listingId, locale, router]);

  useScrollLock(isModalOpen);
  useScrollLock(isPreviewOpen);

  return (
    <main className={styles.main}>
      <h2 className={styles.title}>
        {t("title", { complete_name: userData.complete_name })}
      </h2>
      <PageSearchbar locale={locale} sessionId={userData.idToken} />
      <MobileSearchbar sessionId={userData.idToken} />

      {!state.pageLoading &&
      state.listings.length === 0 &&
      (type !== TypeEnum.DEFAULT ||
        purpose !== PurposeEnum.DEFAULT ||
        state.current_suggestion.value !== "" ||
        state.current_suggestion.type !== "") ? (
        <h2 className={styles.noListings}>{t("noListingsMatch")}</h2>
      ) : !state.pageLoading &&
        state.listings.length === 0 &&
        type === TypeEnum.DEFAULT &&
        purpose === PurposeEnum.DEFAULT &&
        state.current_suggestion.value === "" &&
        state.current_suggestion.type === "" ? (
        <h2 className={styles.noListings}>{t("noListings")}</h2>
      ) : null}

      <div className={styles.pageWrapper}>
        {state.pageLoading && (
          <ul className={styles.listingsGrid}>
            {[...Array(12)].map((_, i) => {
              return (
                <li className={styles.listingItem} key={i}>
                  <ListingCardLayoutLoading>
                    <ListingCardLoading
                      hasModifyButton={false}
                      hasRoundButtons={false}
                      isResizable={false}
                      hasActions={true}
                      hasStatusLabel={true}
                    />
                  </ListingCardLayoutLoading>
                </li>
              );
            })}{" "}
          </ul>
        )}
        <ul className={styles.listingsGrid}>
          {state.listings.map((listing, index) => {
            console.log(listing.expires_at);
            const expiresAt = new Date(listing.expires_at * 1000);
            console.log(expiresAt);
            return (
              <li className={styles.listingItem} key={index}>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    duration: 0.3,
                    delay: 0.0,
                    ease: "easeIn",
                  }}
                >
                  {isRefetchingListingCardLoading === listing.listing_id ? (
                    <ListingCardLayoutLoading>
                      <ListingCardLoading
                        hasModifyButton={false}
                        hasRoundButtons={false}
                        isResizable={false}
                        hasActions={true}
                        hasStatusLabel={true}
                      />
                    </ListingCardLayoutLoading>
                  ) : (
                    <ListingCardLayout
                      listing_id={listing.listing_id}
                      key={index}
                      locale={locale}
                      onPreviewTap={() => {
                        setPreviewListingId(listing.listing_id);
                        setIsPreviewOpen(true);
                      }}
                      openModal={() => {
                        setIsModalOpen(true);
                        setSettingsModalListing(listing);
                      }}
                    >
                      <GeneralListingCard
                        data={listing}
                        isResizable={false}
                        hasActions={true}
                        hasStatusLabel={true}
                        hasTypeLabel={true}
                        statusLabelText={
                          !expiresAt || expiresAt.getTime() < Date.now()
                            ? t("inactive")
                            : `${t("active")} ${expiresAt.toLocaleDateString()}`
                        }
                        hasRoundButtons={false}
                      />
                    </ListingCardLayout>
                  )}
                </motion.div>
              </li>
            );
          })}
        </ul>
        {isPreviewOpen && (
          <PreviewFrame
            listingId={previewListingId}
            onClose={() => setIsPreviewOpen(false)}
            isXs={isXs}
            isSm={isSm}
            isMd={isMd}
            isLg={isLg}
          />
        )}
        {/* {has_more_pages_front_end && (
          <button
            disabled={loadingNextPageData}
            onClick={async (e) => {
              await fetchNextPage(locale, userData.sessionId);
            }}
            className={styles.loadButton}
          >
            Load More
          </button>
        )} */}
        {state.total_count > 0 && (
          <div className={styles.paginationWrapper}>
            <Pagination
              total={state.total_count}
              pageSize={16}
              currentPage={state.page_number}
              onPageChange={async (number) => {
                if (number === state.page_number || state.pageLoading) return;
                await fetchPage(locale, number, userData.idToken);
              }}
            />
          </div>
        )}
        {isModalOpen && settingsModalListing != null && (
          <ListingSettingModal
            listingData={settingsModalListing}
            dialogRef={dialogRef}
            sessionId={userData.idToken}
            closeCallbackAction={() => {
              setIsModalOpen(false);
              setSettingsModalListing(null);
            }}
          />
        )}
      </div>
      <DeleteModalWrapper />
    </main>
  );
}
